type BuildReportSectionTypesInput = {
  isIdSectionUnverifiable: boolean;
  isSsnSectionUnverifiable: boolean;
  isEmployerSectionUnverifiable: boolean;
  isBankSectionUnverifiable: boolean;
};

type BankSourceLike = {
  availableToUpload?: string | null;
  files?: any[] | null;
  fileCount?: number | string | null;
  bank_statement_number?: number | string | null;
  source_id?: number | string | null;
};

type BankSocrStatusMap = Record<string, number | string | null | undefined>;
type BankValidationOtherCheckMap = Record<
  string,
  { SOCRStatus?: number | string | null | undefined } | null | undefined
>;

type DeriveBankRiskStateInput = {
  bankSources?: BankSourceLike[] | null;
  bankSocrStatus?: BankSocrStatusMap | null;
  bankValidationOtherCheck?: BankValidationOtherCheckMap | null;
};

type DeriveBankRiskStateResult = {
  hasBankDocumentContext: boolean;
  bankSourceStatuses: number[];
  isBankSectionUnverifiable: boolean;
};

type RiskIdSource = {
  license_id?: string | null;
  passport_no?: string | null;
  uscis_number?: string | null;
  backfile?: string | null;
};

const hasSourceDocumentContext = (bankSource: BankSourceLike): boolean => {
  const filesLength = Array.isArray(bankSource?.files) ? bankSource.files.length : 0;
  const fileCount = Number(bankSource?.fileCount ?? 0);
  return (
    bankSource?.availableToUpload === "Yes" && (filesLength > 0 || fileCount > 0)
  );
};

const getSourceNumber = (bankSource: BankSourceLike, sourceIndex: number): number => {
  const sourceNumber = Number(
    bankSource?.bank_statement_number ?? bankSource?.source_id
  );
  if (Number.isFinite(sourceNumber) && sourceNumber > 0) {
    return Math.trunc(sourceNumber);
  }
  return sourceIndex + 1;
};

export const deriveBankRiskState = ({
  bankSources = [],
  bankSocrStatus = {},
  bankValidationOtherCheck = {},
}: DeriveBankRiskStateInput): DeriveBankRiskStateResult => {
  const sources = Array.isArray(bankSources) ? bankSources : [];

  const bankSourceStatuses = sources
    .map((bankSource, sourceIndex) => {
      if (!hasSourceDocumentContext(bankSource || {})) {
        return null;
      }
      const sourceNumber = getSourceNumber(bankSource || {}, sourceIndex);
      const validationStatus =
        bankValidationOtherCheck?.[`source_${sourceNumber}`]?.SOCRStatus;
      const bankStatus = bankSocrStatus?.[`Source_${sourceNumber}`];
      const normalizedStatus = Number(bankStatus ?? validationStatus);
      return Number.isFinite(normalizedStatus) ? normalizedStatus : null;
    })
    .filter((status): status is number => status !== null && status !== undefined);

  const hasBankDocumentContext = sources.some((bankSource) =>
    hasSourceDocumentContext(bankSource || {})
  );

  return {
    hasBankDocumentContext,
    bankSourceStatuses,
    isBankSectionUnverifiable:
      hasBankDocumentContext &&
      bankSourceStatuses.some((status) => status === 2),
  };
};

export const buildReportSectionTypes = ({
  isIdSectionUnverifiable,
  isSsnSectionUnverifiable,
  isEmployerSectionUnverifiable,
  isBankSectionUnverifiable,
}: BuildReportSectionTypesInput): string[] => {
  return [
    isIdSectionUnverifiable ? "ID" : null,
    isSsnSectionUnverifiable ? "SSN" : null,
    isEmployerSectionUnverifiable ? "Paystub" : null,
    isBankSectionUnverifiable ? "Bank" : null,
  ].filter(Boolean) as string[];
};

export const deriveRiskIdDocumentNumber = (
  identificationData?: RiskIdSource | null
): string => {
  return String(identificationData?.license_id ?? "").trim();
};

export const shouldIncludeRiskIdDocumentNumberInPayload = ({
  normalizedIdDocumentNumber,
  identificationData,
}: {
  normalizedIdDocumentNumber?: string | null;
  identificationData?: RiskIdSource | null;
}): boolean => {
  const normalized = String(normalizedIdDocumentNumber ?? "").trim();
  if (!normalized) {
    return false;
  }

  const hasBackBarcodeContext = String(identificationData?.backfile ?? "").trim().length > 0;
  return hasBackBarcodeContext;
};

export const shouldReportIdFraudSection = (input: {
  isIdSectionUnverifiable: boolean;
  normalizedIdDocumentNumber?: string | null;
  identificationData?: RiskIdSource | null;
}): boolean => input.isIdSectionUnverifiable;

export const buildRiskApplicantPayload = ({
  firstName,
  lastName,
  email,
  phone,
  ssn,
  normalizedIdDocumentNumber,
  identificationData,
  includeNameOnly = false,
}: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  ssn?: string | null;
  normalizedIdDocumentNumber?: string | null;
  identificationData?: RiskIdSource | null;
  includeNameOnly?: boolean;
}): Record<string, string | string[]> => {
  const payload: Record<string, string | string[]> = {};
  const normalizedFirstName = String(firstName ?? "").trim();
  const normalizedLastName = String(lastName ?? "").trim();
  const normalizedEmail = String(email ?? "").trim();
  const normalizedPhone = String(phone ?? "").trim();
  const normalizedSsn = String(ssn ?? "").trim();

  if (normalizedEmail) payload.email = normalizedEmail;
  if (normalizedPhone) payload.phone = normalizedPhone;
  if (normalizedSsn) payload.ssn = normalizedSsn;

  if (
    shouldIncludeRiskIdDocumentNumberInPayload({
      normalizedIdDocumentNumber,
      identificationData,
    })
  ) {
    payload.idDocumentNumber = String(normalizedIdDocumentNumber ?? "").trim();
  }

  if (
    (Object.keys(payload).length > 0 || includeNameOnly) &&
    normalizedFirstName &&
    normalizedLastName
  ) {
    payload.firstName = normalizedFirstName;
    payload.lastName = normalizedLastName;
  }

  return payload;
};
